// CreatorMap.js
// Public Expo WEB screen using the existing GetCreatorMap.php response.
// No additional npm packages required.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Platform,
  View,
  Text,
  Pressable,
  Linking,
} from 'react-native';

import { useIsFocused } from '@react-navigation/native';

const ORIGIN = 'https://collabbay.com';
const API = `${ORIGIN}/APIControllers/GetCreatorMap.php`;

const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

const TILE_CREDIT =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

const BATCH = 12;

let leafletPromise;

/* -------------------------------------------------------------------------- */
/* Map library                                                                */
/* -------------------------------------------------------------------------- */

function asset(id, url, css = false, integrity = '') {
  return new Promise((resolve, reject) => {
    let el = document.getElementById(id);

    if (el?.dataset.ready === '1' || (css && el?.sheet)) {
      resolve();
      return;
    }

    if (el?.dataset.failed === '1') {
      el.remove();
      el = null;
    }

    const fresh = !el;

    el = el || document.createElement(css ? 'link' : 'script');

    let timer;

    const clean = () => {
      clearTimeout(timer);
      el.removeEventListener('load', loaded);
      el.removeEventListener('error', failed);
    };

    const loaded = () => {
      el.dataset.ready = '1';
      clean();
      resolve();
    };

    const failed = () => {
      el.dataset.failed = '1';
      clean();
      reject(
        new Error('The map library could not load. Please retry.')
      );
    };

    el.addEventListener('load', loaded);
    el.addEventListener('error', failed);

    timer = setTimeout(failed, 16000);

    if (fresh) {
      el.id = id;

      if (css) {
        el.rel = 'stylesheet';
        el.href = url;
      } else {
        el.src = url;
        el.async = true;
      }

      if (integrity) {
        el.integrity = integrity;
        el.crossOrigin = 'anonymous';
      }

      document.head.appendChild(el);
    }
  });
}

function loadMapLibrary() {
  if (!leafletPromise) {
    leafletPromise = (async () => {
      await asset(
        'cmap-leaflet-css',
        'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css',
        true,
        'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY='
      );

      if (!window.L) {
        await asset(
          'cmap-leaflet-js',
          'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js',
          false,
          'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo='
        );
      }

      if (!window.L) {
        throw new Error('The map library is unavailable.');
      }

      try {
        await asset(
          'cmap-clusters-css',
          'https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css',
          true
        );

        if (!window.L.markerClusterGroup) {
          await asset(
            'cmap-clusters-js',
            'https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js'
          );
        }
      } catch (_) {
        // Ordinary pins remain usable if clustering is unavailable.
      }

      return window.L;
    })().catch(error => {
      leafletPromise = null;
      throw error;
    });
  }

  return leafletPromise;
}

/* -------------------------------------------------------------------------- */
/* Data helpers                                                               */
/* -------------------------------------------------------------------------- */

function safeUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return '';

  try {
    const url = new URL(value.trim(), ORIGIN);

    if (
      !['https:', 'http:'].includes(url.protocol) ||
      url.username ||
      url.password
    ) {
      return '';
    }

    if (
      ['collabbay.com', 'www.collabbay.com'].includes(url.hostname) &&
      !url.port
    ) {
      url.protocol = 'https:';
    }

    return url.href;
  } catch (_) {
    return '';
  }
}

function creatorPath(name) {
  return '/creatormap' + (name ? '/' + encodeURIComponent(name) : '');
}

function kindOf(item) {
  if (['video', 'image', 'link'].includes(item?.kind)) {
    return item.kind;
  }

  try {
    const url = new URL(item.url);

    if (
      /(^|\.)(instagram\.com|tiktok\.com|youtube\.com|youtu\.be|facebook\.com|fb\.watch|vimeo\.com|x\.com|twitter\.com)$/.test(
        url.hostname
      )
    ) {
      return 'link';
    }

    if (
      /\.(mp4|m4v|webm|mov|ogv|ogg|avi|mkv|m3u8)$/i.test(url.pathname)
    ) {
      return 'video';
    }

    if (
      /\.(jpe?g|png|webp|gif|avif|svg|bmp|heic|heif)$/i.test(url.pathname)
    ) {
      return 'image';
    }

    return 'unknown';
  } catch (_) {
    return 'link';
  }
}

function ratingScore(item) {
  return (
    { A: 1, AA: 2, AAA: 3 }[
      String(item?.creator_rating || '').trim().toUpperCase()
    ] ||
    (item?.featured ? 1 : 0)
  );
}

function previewOf(venue) {
  return venue.media[0];
}

function mapPreviewOf(venue) {
  return (
    venue.media.find(m => m.poster || kindOf(m) === 'image') ||
    previewOf(venue)
  );
}

function posterOf(venue) {
  const item = previewOf(venue);

  return (
    item?.poster ||
    (kindOf(item) === 'image' ? item.url : '')
  );
}

function initials(name) {
  return String(name || 'C')
    .replace(/^@/, '')
    .slice(0, 2)
    .toUpperCase();
}

function plural(n, word) {
  return `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;
}

function dateScore(value) {
  return Date.parse(value || '') || 0;
}

function distanceKm(a, b) {
  const rad = n => (n * Math.PI) / 180;

  const h =
    Math.sin(rad(b.lat - a.lat) / 2) ** 2 +
    Math.cos(rad(a.lat)) *
      Math.cos(rad(b.lat)) *
      Math.sin(rad(b.lng - a.lng) / 2) ** 2;

  return (
    6371.0088 *
    2 *
    Math.atan2(
      Math.sqrt(Math.min(1, h)),
      Math.sqrt(Math.max(0, 1 - h))
    )
  );
}

function distanceLabel(km) {
  return km < 1
    ? `${Math.round(km * 1000)} m away`
    : `${km.toFixed(1)} km away`;
}

function normalise(payload) {
  if (
    !Array.isArray(payload?.venues) ||
    !Array.isArray(payload?.creators)
  ) {
    throw new Error('Unexpected API response.');
  }

  const venues = payload.venues
    .filter(v => v && typeof v === 'object')
    .map(v => ({
      ...v,
      id: String(v.id),
      name: String(v.name || 'Venue'),
      lat: Number(v.lat),
      lng: Number(v.lng),
      area: String(v.area || ''),
      city: String(v.city || ''),
      address: String(v.address || ''),
      booking_url: safeUrl(v.booking_url),
      media: (Array.isArray(v.media) ? v.media : [])
        .filter(Boolean)
        .map(m => ({
          ...m,
          creator: String(m.creator || ''),
          url: safeUrl(m.url),
          poster: safeUrl(m.poster),
          original_url: safeUrl(m.original_url || m.url),
        }))
        .filter(m => m.url && m.creator)
        .sort(
          (a, b) =>
            ratingScore(b) - ratingScore(a) ||
            dateScore(b.uploaded_at) - dateScore(a.uploaded_at)
        ),
    }))
    .filter(
      v =>
        v.media.length &&
        Number.isFinite(v.lat) &&
        Number.isFinite(v.lng) &&
        Math.abs(v.lat) <= 85.051129 &&
        Math.abs(v.lng) <= 180
    );

  return {
    venues,
    creators: [
      ...new Set(
        payload.creators.filter(
          c => typeof c === 'string' && c.trim()
        )
      ),
    ],
  };
}

function buildFeedPosts(venues) {
  const result = [];
  const queues = venues.map(venue => ({ venue, index: 0 }));

  let remaining = true;

  while (remaining) {
    remaining = false;

    queues.forEach(q => {
      const i = q.index++;
      const item = q.venue.media[i];

      if (!item) return;

      remaining = true;

      result.push({
        venue: q.venue,
        item,
        id: `${q.venue.id}:${i}:${item.url}`,
      });
    });
  }

  return result;
}

/* -------------------------------------------------------------------------- */
/* Map previews                                                               */
/* -------------------------------------------------------------------------- */

function pinShape(venue) {
  const hash = [...venue.id].reduce(
    (n, c) => (n * 31 + c.charCodeAt(0)) >>> 0,
    0
  );

  const shapes = [
    [66, 78],
    [72, 62],
    [58, 68],
    [64, 64],
  ];

  return ratingScore(previewOf(venue)) >= 2
    ? [76, 88]
    : shapes[hash % shapes.length];
}

function appendMapStill(parent, item, fallback = '') {
  const picture = safeUrl(
    item?.poster ||
      (kindOf(item) === 'image' ? item?.url : '')
  );

  if (!picture) {
    parent.textContent = initials(fallback);
    return;
  }

  const img = document.createElement('img');

  img.src = picture;
  img.alt = '';
  img.loading = 'lazy';
  img.decoding = 'async';

  img.onerror = () => {
    img.remove();
    parent.textContent = initials(fallback);
  };

  parent.appendChild(img);
}

function clusterIcon(L, cluster) {
  const markers = cluster.getAllChildMarkers();

  const venues = markers
    .map(m => m.options.cmapVenue)
    .filter(Boolean)
    .sort(
      (a, b) =>
        ratingScore(previewOf(b)) - ratingScore(previewOf(a))
    );

  const media = venues
    .flatMap(venue =>
      venue.media.map(item => ({ item, venue }))
    )
    .filter(
      ({ item }) => item.poster || kindOf(item) === 'image'
    );

  const face = document.createElement('div');
  face.className = 'cmap-cluster-stack';

  if (!media.length) {
    face.classList.add('is-count');
    face.textContent = String(markers.length);

    return L.divIcon({
      className: 'cmap-cluster',
      html: face,
      iconSize: [42, 42],
      iconAnchor: [21, 21],
    });
  }

  media
    .slice(0, 3)
    .reverse()
    .forEach(({ item, venue }, i, arr) => {
      const thumb = document.createElement('span');

      thumb.className = 'cmap-cluster-photo';
      thumb.style.setProperty(
        '--stack',
        String(arr.length - i - 1)
      );

      appendMapStill(thumb, item, venue.name);
      face.appendChild(thumb);
    });

  const count = document.createElement('span');

  count.className = 'cmap-cluster-count';
  count.textContent = '+' + (markers.length - 1);

  face.appendChild(count);

  return L.divIcon({
    className: 'cmap-cluster',
    html: face,
    iconSize: [76, 82],
    iconAnchor: [38, 72],
  });
}

/* -------------------------------------------------------------------------- */
/* UI helpers                                                                 */
/* -------------------------------------------------------------------------- */

function Icon({ name, size = 19 }) {
  const paths = {
    search: (
      <>
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="m16 16 5 5" />
      </>
    ),
    map: (
      <>
        <path d="m3 5 6-2 6 2 6-2v16l-6 2-6-2-6 2ZM9 3v16m6-14v16" />
      </>
    ),
    feed: (
      <>
        <rect x="4" y="3" width="16" height="7" rx="2" />
        <rect x="4" y="14" width="16" height="7" rx="2" />
      </>
    ),
    near: (
      <>
        <circle cx="12" cy="12" r="7" />
        <circle cx="12" cy="12" r="2" />
        <path d="M12 1v4m0 14v4M1 12h4m14 0h4" />
      </>
    ),
    pin: (
      <>
        <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
        <circle cx="12" cy="10" r="2.5" />
      </>
    ),
    play: <path d="m9 5 10 7-10 7Z" />,
    close: <path d="m6 6 12 12M18 6 6 18" />,
    share: (
      <path d="M12 16V3m-5 5 5-5 5 5M5 13v7h14v-7" />
    ),
    arrow: <path d="M5 12h14m-6-6 6 6-6 6" />,
    external: <path d="M8 5h11v11M19 5 5 19" />,
    fit: <path d="M9 3H3v6m12-6h6v6M3 15v6h6m12-6v6h-6" />,
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name] || paths.pin}
    </svg>
  );
}

function External({
  href,
  children,
  className = '',
  ...rest
}) {
  const url = safeUrl(href);

  if (!url) return null;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      {...rest}
    >
      {children}
    </a>
  );
}

/* -------------------------------------------------------------------------- */
/* Media                                                                      */
/* -------------------------------------------------------------------------- */

function Media({
  item,
  label,
  active = false,
  enabled = true,
  controls = false,
  poster = '',
}) {
  const box = useRef(null);
  const player = useRef(null);

  const [near, setNear] = useState(false);
  const [visible, setVisible] = useState(false);
  const [failed, setFailed] = useState(0);
  const [blocked, setBlocked] = useState(false);
  const [ratio, setRatio] = useState(4 / 5);

  const type = kindOf(item);
  const url = safeUrl(item?.url);

  const effective =
    type === 'unknown'
      ? failed === 0
        ? 'image'
        : 'video'
      : type;

  const broken =
    type === 'unknown' ? failed >= 2 : failed >= 1;

  useEffect(() => {
    setFailed(0);
    setBlocked(false);
    setRatio(4 / 5);
  }, [url]);

  useEffect(() => {
    if (!box.current) return undefined;

    if (!window.IntersectionObserver) {
      setNear(true);
      setVisible(true);
      return undefined;
    }

    const warm = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setNear(true);
          warm.disconnect();
        }
      },
      { rootMargin: '700px' }
    );

    const watch = new IntersectionObserver(
      ([entry]) => {
        setVisible(
          entry.isIntersecting &&
            entry.intersectionRatio >= 0.4
        );
      },
      { threshold: [0, 0.4, 1] }
    );

    warm.observe(box.current);
    watch.observe(box.current);

    return () => {
      warm.disconnect();
      watch.disconnect();
    };
  }, []);

  useEffect(() => {
    const video = player.current;

    if (!video) return undefined;

    if (
      active &&
      enabled &&
      visible &&
      !document.hidden
    ) {
      video.muted = true;
      video.play()?.catch(() => setBlocked(true));
    } else {
      video.pause();
    }

    return () => video.pause();
  }, [
    active,
    enabled,
    visible,
    near,
    effective,
    broken,
    url,
  ]);

  useEffect(() => {
    const pause = () => {
      if (document.hidden) {
        player.current?.pause();
      }
    };

    document.addEventListener('visibilitychange', pause);

    return () => {
      document.removeEventListener(
        'visibilitychange',
        pause
      );
    };
  }, []);

  const onPlay = event => {
    document
      .querySelectorAll('video[data-cmap-video]')
      .forEach(video => {
        if (video !== event.currentTarget) {
          video.pause();
        }
      });

    setBlocked(false);
  };

  return (
    <div
      className="cmap-media"
      ref={box}
      style={{ '--media-ratio': ratio }}
    >
      {!url || type === 'link' || broken ? (
        <div className="cmap-media-empty">
          <Icon name="play" size={30} />

          <strong>
            {type === 'link'
              ? 'See the original post'
              : 'Preview unavailable'}
          </strong>

          <External href={item?.original_url || url}>
            Open original
            <Icon name="external" size={14} />
          </External>
        </div>
      ) : !near ? (
        <div className="cmap-media-placeholder">
          <Icon
            name={effective === 'video' ? 'play' : 'pin'}
            size={26}
          />
        </div>
      ) : effective === 'video' ? (
        <>
          <video
            ref={player}
            data-cmap-video
            src={url}
            poster={
              safeUrl(item?.poster || poster) || undefined
            }
            controls={controls}
            muted
            loop
            playsInline
            preload="metadata"
            aria-label={label}
            onLoadedMetadata={event => {
              const video = event.currentTarget;

              if (video.videoWidth && video.videoHeight) {
                setRatio(
                  video.videoWidth / video.videoHeight
                );
              }
            }}
            onPlay={onPlay}
            onError={() => setFailed(n => n + 1)}
          />

          {!controls && blocked && (
            <button
              type="button"
              className="cmap-play"
              aria-label="Play preview"
              onClick={() =>
                player.current
                  ?.play()
                  ?.catch(() => setBlocked(true))
              }
            >
              <Icon name="play" />
            </button>
          )}

          <span className="cmap-video-tag">
            <Icon name="play" size={10} />
            VIDEO
          </span>
        </>
      ) : (
        <img
          src={url}
          alt={label}
          loading="lazy"
          decoding="async"
          onLoad={event => {
            const img = event.currentTarget;

            if (img.naturalHeight) {
              setRatio(
                img.naturalWidth / img.naturalHeight
              );
            }
          }}
          onError={() => setFailed(n => n + 1)}
        />
      )}
    </div>
  );
}

function StillPreview({ item, poster = '' }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [item?.url, item?.poster, poster]);

  const picture = safeUrl(
    item?.poster ||
      poster ||
      (kindOf(item) === 'image' ? item?.url : '')
  );

  if (failed) {
    return (
      <div
        className="cmap-still cmap-still-fallback"
        aria-hidden="true"
      >
        <Icon name="play" size={22} />
      </div>
    );
  }

  if (picture) {
    return (
      <img
        className="cmap-still"
        src={picture}
        loading="lazy"
        decoding="async"
        alt=""
        onError={() => setFailed(true)}
      />
    );
  }

  if (kindOf(item) === 'video') {
    return (
      <video
        className="cmap-still"
        src={safeUrl(item?.url)}
        muted
        playsInline
        preload="metadata"
        aria-hidden="true"
        tabIndex={-1}
        onError={() => setFailed(true)}
        onLoadedMetadata={event => {
          const video = event.currentTarget;

          if (
            Number.isFinite(video.duration) &&
            video.duration > 0.2
          ) {
            try {
              video.currentTime = Math.min(
                0.25,
                video.duration / 3
              );
            } catch (_) {}
          }
        }}
        onSeeked={event => event.currentTarget.pause()}
      />
    );
  }

  return (
    <div
      className="cmap-still cmap-still-fallback"
      aria-hidden="true"
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Venue details                                                              */
/* -------------------------------------------------------------------------- */

function VenueDialog({
  venue,
  onClose,
  onCreator,
  focused,
}) {
  const dialog = useRef(null);
  const close = useRef(null);

  useEffect(() => {
    const previous = document.activeElement;

    close.current?.focus();

    const key = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }

      if (event.key !== 'Tab') return;

      const nodes = [
        ...dialog.current.querySelectorAll(
          'button:not(:disabled),a[href],video[controls]'
        ),
      ].filter(node => node.getClientRects().length);

      const first = nodes[0];
      const last = nodes[nodes.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === first
      ) {
        event.preventDefault();
        last?.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', key);

    return () => {
      document.removeEventListener('keydown', key);

      if (previous?.isConnected) {
        previous.focus();
      }
    };
  }, [onClose]);

  const directions =
    'https://www.google.com/maps/dir/?api=1&destination=' +
    encodeURIComponent(`${venue.lat},${venue.lng}`);

  return (
    <div
      className="cmap-shade"
      onMouseDown={event => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section
        ref={dialog}
        className="cmap-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cmap-venue-title"
      >
        <header className="cmap-dialog-head">
          <p className="cmap-eyebrow">
            THROUGH THE CREATOR'S EYES
          </p>

          <h2 id="cmap-venue-title">{venue.name}</h2>

          <p className="cmap-address">
            {[venue.address, venue.area, venue.city]
              .filter(Boolean)
              .join(' · ')}
          </p>

          <button
            ref={close}
            type="button"
            className="cmap-icon-button cmap-dialog-close"
            aria-label="Close venue"
            onClick={onClose}
          >
            <Icon name="close" />
          </button>

          <div className="cmap-dialog-actions">
            <External
              href={venue.booking_url}
              className="cmap-button cmap-dark"
            >
              Book / visit website
              <Icon name="external" size={16} />
            </External>

            <External
              href={directions}
              className="cmap-button"
            >
              <Icon name="near" size={16} />
              Directions
            </External>

            <span>
              {plural(venue.media.length, 'post')}
            </span>
          </div>
        </header>

        <div className="cmap-gallery">
          {venue.media.map(item => (
            <article
              key={`${item.creator}-${item.url}`}
            >
              <div className="cmap-byline">
                <span className="cmap-avatar">
                  {initials(item.creator)}
                </span>

                <button
                  type="button"
                  onClick={() => onCreator(item.creator)}
                >
                  {item.creator}
                </button>
              </div>

              <Media
                item={item}
                label={`${venue.name}, by ${item.creator}`}
                enabled={focused}
                controls
              />

              <External
                href={item.original_url || item.url}
                className="cmap-original"
              >
                Open original
                <Icon name="external" size={13} />
              </External>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Main screen                                                                */
/* -------------------------------------------------------------------------- */

function CreatorMapWeb({ route, navigation }) {
  const focused = useIsFocused();

  const creator =
    typeof route?.params?.Promoter === 'string'
      ? route.params.Promoter.trim()
      : '';

  const [mode, setMode] = useState('map');

  const [data, setData] = useState({
    key: creator,
    creators: [],
    venues: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);

  const [query, setQuery] = useState('');
  const [location, setLocation] = useState(null);
  const [radius, setRadius] = useState(10);

  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState('');

  const [selected, setSelected] = useState(null);
  const [peek, setPeek] = useState(null);

  const [limit, setLimit] = useState(BATCH);
  const [notice, setNotice] = useState('');
  const [activePost, setActivePost] = useState('');

  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState('');
  const [mapRetry, setMapRetry] = useState(0);

  const [viewport, setViewport] = useState({
    lat: 51.5074,
    lng: -0.1278,
    tick: 0,
  });

  const rootNode = useRef(null);
  const headerNode = useRef(null);
  const mapNode = useRef(null);
  const mapState = useRef(null);
  const feedNode = useRef(null);
  const sentinel = useRef(null);

  const enterTimer = useRef(null);
  const leaveTimer = useRef(null);
  const toastTimer = useRef(null);
  const geoRequest = useRef(0);

  const alive = useRef(true);
  const latest = useRef({});
  const pendingFit = useRef(true);
  const target = useRef(null);
  const reduced = useRef(false);

  const closeDialog = useCallback(() => {
    setSelected(null);
  }, []);

  const dismissPeek = useCallback(() => {
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);
    setPeek(null);
  }, []);

  const chooseCreator = useCallback(
    name => {
      setSelected(null);
      dismissPeek();
      setQuery('');

      navigation.navigate('CreatorMap', {
        Promoter: name || undefined,
      });
    },
    [navigation, dismissPeek]
  );

  const toast = useCallback(text => {
    clearTimeout(toastTimer.current);
    setNotice(text);

    toastTimer.current = setTimeout(
      () => setNotice(''),
      3200
    );
  }, []);

  useEffect(() => {
    alive.current = true;

    reduced.current = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    return () => {
      alive.current = false;
      geoRequest.current++;

      clearTimeout(enterTimer.current);
      clearTimeout(leaveTimer.current);
      clearTimeout(toastTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!focused) {
      dismissPeek();
      return undefined;
    }

    const old = document.title;

    document.title = creator
      ? `${creator}'s map | collabbay`
      : 'The creator map | collabbay';

    return () => {
      document.title = old;
    };
  }, [creator, focused, dismissPeek]);

  /* Fetch existing PHP endpoint. */

  useEffect(() => {
    const controller = new AbortController();

    let current = true;
    let timedOut = false;

    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 25000);

    setLoading(true);
    setError('');
    setSelected(null);
    dismissPeek();

    setData(old => ({
      key: creator,
      creators: old.creators,
      venues: [],
    }));

    (async () => {
      try {
        const url = new URL(API);

        if (creator) {
          url.searchParams.set('creator', creator);
        }

        const response = await fetch(url.href, {
          signal: controller.signal,
          credentials: 'omit',
          headers: { Accept: 'application/json' },
        });

        const text = await response.text();

        let payload;

        try {
          payload = JSON.parse(text);
        } catch (_) {
          throw new Error(
            'The API returned invalid JSON. Check the PHP URL.'
          );
        }

        if (!response.ok || !payload?.ok) {
          throw new Error(
            `${payload?.message || 'Could not load the map.'}${
              payload?.reference
                ? ` Reference: ${payload.reference}`
                : ''
            }`
          );
        }

        const clean = normalise(payload);

        if (current) {
          setData({ ...clean, key: creator });
        }
      } catch (err) {
        if (current) {
          setError(
            timedOut
              ? 'The request timed out. Please retry.'
              : err.message || 'Could not load the map.'
          );
        }
      } finally {
        clearTimeout(timer);

        if (current) {
          setLoading(false);
        }
      }
    })();

    return () => {
      current = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [creator, reload, dismissPeek]);

  /* Measure header so the feed never sits underneath it. */

  useEffect(() => {
    const measure = () => {
      if (!rootNode.current || !headerNode.current) {
        return;
      }

      const top =
        rootNode.current.getBoundingClientRect().top;

      const bottom =
        headerNode.current.getBoundingClientRect().bottom;

      rootNode.current.style.setProperty(
        '--head',
        `${Math.ceil(bottom - top + 12)}px`
      );
    };

    measure();

    const observer = window.ResizeObserver
      ? new ResizeObserver(measure)
      : null;

    if (observer) {
      observer.observe(headerNode.current);
    }

    window.addEventListener('resize', measure);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const venues = useMemo(() => {
    if (data.key !== creator) return [];

    const term = query.trim().toLocaleLowerCase();

    return data.venues
      .map(venue => ({
        ...venue,
        distance: location
          ? distanceKm(location, venue)
          : null,
      }))
      .filter(venue => {
        const matchesSearch =
          !term ||
          [
            venue.name,
            venue.area,
            venue.city,
            venue.address,
            ...venue.media.map(item => item.creator),
          ]
            .join(' ')
            .toLocaleLowerCase()
            .includes(term);

        const matchesRadius =
          !location ||
          radius === 0 ||
          venue.distance <= radius;

        return matchesSearch && matchesRadius;
      })
      .sort((a, b) => {
        if (location) {
          return a.distance - b.distance;
        }

        return (
          Number(
            b.featured || ratingScore(previewOf(b))
          ) -
            Number(
              a.featured || ratingScore(previewOf(a))
            ) ||
          dateScore(b.latest_at) - dateScore(a.latest_at) ||
          a.name.localeCompare(b.name)
        );
      });
  }, [data, creator, query, location, radius]);

  const posts = useMemo(
    () => buildFeedPosts(venues),
    [venues]
  );

  const postCount = posts.length;

  const mapNearby = useMemo(() => {
    const map = mapState.current?.map;

    const onScreen = map
      ? venues.filter(venue =>
          map
            .getBounds()
            .pad(0.1)
            .contains([venue.lat, venue.lng])
        )
      : venues;

    const pool = onScreen.length ? onScreen : venues;

    return [...pool]
      .sort(
        (a, b) =>
          distanceKm(viewport, a) -
            distanceKm(viewport, b) ||
          Number(b.featured || 0) -
            Number(a.featured || 0)
      )
      .slice(0, 5);
  }, [venues, viewport, mapReady]);

  latest.current = {
    venues,
    location,
    mode,
    focused,
    loading,
  };

  useEffect(() => {
    setLimit(BATCH);
    setActivePost('');
    setSelected(null);
    dismissPeek();

    if (feedNode.current) {
      feedNode.current.scrollTop = 0;
    }
  }, [venues, dismissPeek]);

  /* Map positioning. */

  const fitMap = useCallback(() => {
    const state = mapState.current;

    if (!state || latest.current.mode !== 'map') {
      pendingFit.current = true;
      return;
    }

    const points = latest.current.venues.map(venue => [
      venue.lat,
      venue.lng,
    ]);

    if (latest.current.location) {
      points.push([
        latest.current.location.lat,
        latest.current.location.lng,
      ]);
    }

    if (!points.length) {
      pendingFit.current = false;
      return;
    }

    const mobile = window.matchMedia(
      '(max-width: 760px)'
    ).matches;

    const height = state.map.getSize().y;

    const topPad = Math.min(
      (headerNode.current?.offsetHeight || 60) + 100,
      height * 0.28
    );

    const bottomPad = Math.min(
      mobile ? 275 : 300,
      height * 0.28
    );

    if (points.length === 1) {
      state.map.setView(points[0], 15, {
        animate: !reduced.current,
      });
    } else {
      state.map.fitBounds(points, {
        paddingTopLeft: [45, topPad],
        paddingBottomRight: [45, bottomPad],
        maxZoom: 15,
        animate: !reduced.current,
      });
    }

    pendingFit.current = false;
  }, []);

  const showPeek = useCallback((venue, marker) => {
    const state = mapState.current;

    if (
      !state ||
      !latest.current.focused ||
      latest.current.mode !== 'map'
    ) {
      return;
    }

    const point = state.map.latLngToContainerPoint(
      marker.getLatLng()
    );

    const size = state.map.getSize();

    setPeek({
      venue,
      x: Math.max(
        12,
        Math.min(size.x - 248, point.x - 120)
      ),
      y: Math.max(
        115,
        Math.min(
          size.y - 360,
          point.y > 430 ? point.y - 305 : point.y + 18
        )
      ),
    });
  }, []);

  const leavePeek = useCallback(() => {
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);

    leaveTimer.current = setTimeout(
      () => setPeek(null),
      230
    );
  }, []);

  /* Initialise Leaflet. */

  useEffect(() => {
    let cancelled = false;
    let observer;
    let frame;
    let localState;

    setMapReady(false);
    setMapError('');

    loadMapLibrary()
      .then(L => {
        if (cancelled || !mapNode.current) return;

        const map = L.map(mapNode.current, {
          zoomControl: false,
          preferCanvas: true,
        }).setView([51.5074, -0.1278], 12);

        const tiles = L.tileLayer(TILE_URL, {
          maxZoom: 19,
          attribution: TILE_CREDIT,
        }).addTo(map);

        L.control
          .zoom({ position: 'topright' })
          .addTo(map);

        const group = L.markerClusterGroup
          ? L.markerClusterGroup({
              showCoverageOnHover: false,
              maxClusterRadius: zoom =>
                zoom < 11 ? 48 : zoom < 14 ? 32 : 23,
              disableClusteringAtZoom: 17,
              spiderfyOnMaxZoom: true,
              animate: !reduced.current,
              iconCreateFunction: cluster =>
                clusterIcon(L, cluster),
            })
          : L.featureGroup();

        group.addTo(map);

        localState = {
          L,
          map,
          group,
          user: L.layerGroup().addTo(map),
          markers: new Map(),
        };

        mapState.current = localState;

        map.on('movestart', dismissPeek);
        map.on('click', dismissPeek);

        const syncViewport = () => {
          const point = map.getCenter();

          if (!cancelled) {
            setViewport(previous => ({
              lat: point.lat,
              lng: point.lng,
              tick: previous.tick + 1,
            }));
          }
        };

        const hydratePins = () => {
          const bounds = map.getBounds().pad(0.13);
          const center = map.getCenter();

          const visible = [
            ...localState.markers.values(),
          ].filter(
            entry =>
              entry.previewVideo &&
              entry.marker.getElement() &&
              bounds.contains(entry.marker.getLatLng())
          );

          visible.sort(
            (a, b) =>
              Number(b.featured || 0) -
                Number(a.featured || 0) ||
              distanceKm(center, a.venue) -
                distanceKm(center, b.venue)
          );

          visible
            .slice(0, 16)
            .forEach(({ previewVideo, url }) => {
              if (!previewVideo.getAttribute('src')) {
                previewVideo.src = url;
                previewVideo.load();
              }
            });
        };

        localState.hydratePins = hydratePins;

        map.on('moveend', syncViewport);
        map.on('moveend', hydratePins);
        map.on('zoomend', syncViewport);

        group.on(
          'animationend spiderfied',
          hydratePins
        );

        localState.releasePins = () => {
          localState.markers.forEach(
            ({ previewVideo }) => {
              if (previewVideo) {
                previewVideo.pause();
                previewVideo.removeAttribute('src');
                previewVideo.load();
              }
            }
          );
        };

        let tileErrors = 0;

        tiles.on('tileerror', () => {
          if (++tileErrors === 6 && !cancelled) {
            setMapError(
              'Some map tiles did not load. Feed is still available.'
            );
          }
        });

        tiles.on('tileload', () => {
          if (tileErrors >= 6 && !cancelled) {
            setMapError('');
          }

          tileErrors = 0;
        });

        const resize = () => {
          cancelAnimationFrame(frame);

          frame = requestAnimationFrame(() => {
            if (!cancelled) {
              map.invalidateSize();
            }
          });
        };

        if (window.ResizeObserver) {
          observer = new ResizeObserver(resize);
          observer.observe(mapNode.current);
        }

        window.addEventListener('resize', resize);

        localState.stopResize = () => {
          window.removeEventListener('resize', resize);
        };

        setMapReady(true);
      })
      .catch(err => {
        if (!cancelled) {
          setMapError(err.message);
        }
      });

    return () => {
      cancelled = true;

      cancelAnimationFrame(frame);
      observer?.disconnect();

      if (localState) {
        localState.stopResize?.();
        localState.releasePins?.();
        localState.map.remove();

        if (mapState.current === localState) {
          mapState.current = null;
        }
      }
    };
  }, [mapRetry, dismissPeek]);

  /* Thumbnail markers and small secondary image dots. */

  useEffect(() => {
    const state = mapState.current;

    if (!state || !mapReady) return;

    const { L, group, markers } = state;

    state.releasePins?.();
    group.clearLayers();
    markers.clear();

    venues.forEach(venue => {
      const face = document.createElement('span');
      face.className = 'cmap-pin-face';

      const [width, height] = pinShape(venue);

      face.style.width = `${width}px`;
      face.style.height = `${height}px`;

      const item = mapPreviewOf(venue);

      const poster =
        item?.poster ||
        (kindOf(item) === 'image' ? item.url : '');

      let previewVideo = null;

      if (poster) {
        const img = document.createElement('img');

        img.src = poster;
        img.alt = '';
        img.loading = 'lazy';

        img.onerror = () => {
          img.remove();
          face.classList.add('cmap-pin-no-image');
        };

        face.appendChild(img);
      } else if (kindOf(item) === 'video') {
        previewVideo = document.createElement('video');

        previewVideo.muted = true;
        previewVideo.playsInline = true;
        previewVideo.preload = 'metadata';

        previewVideo.setAttribute('muted', '');
        previewVideo.setAttribute('playsinline', '');
        previewVideo.setAttribute('aria-hidden', 'true');

        previewVideo.onloadedmetadata = () => {
          if (
            Number.isFinite(previewVideo.duration) &&
            previewVideo.duration > 0.2
          ) {
            try {
              previewVideo.currentTime = Math.min(
                0.25,
                previewVideo.duration / 3
              );
            } catch (_) {}
          }
        };

        previewVideo.onseeked = () => {
          previewVideo.pause();
        };

        face.appendChild(previewVideo);
      } else {
        face.classList.add('cmap-pin-no-image');
        face.textContent = initials(venue.name);
      }

      const label = document.createElement('span');

      label.className = 'cmap-pin-label';
      label.textContent = venue.name;

      face.appendChild(label);

      if (venue.media.length > 1) {
        const dots = document.createElement('span');
        dots.className = 'cmap-pin-subdots';

        venue.media
          .filter(media => media !== item)
          .sort(
            (a, b) =>
              Number(
                Boolean(
                  b.poster || kindOf(b) === 'image'
                )
              ) -
              Number(
                Boolean(
                  a.poster || kindOf(a) === 'image'
                )
              )
          )
          .slice(0, 2)
          .forEach(media => {
            const dot = document.createElement('span');

            appendMapStill(dot, media, media.creator);
            dots.appendChild(dot);
          });

        face.appendChild(dots);
      }

      const marker = L.marker(
        [venue.lat, venue.lng],
        {
          title: venue.name,
          alt: `${venue.name}: see creator posts`,
          cmapVenue: venue,
          riseOnHover: true,
          icon: L.divIcon({
            className: 'cmap-pin',
            html: face,
            iconSize: [width, height + 12],
            iconAnchor: [width / 2, height + 6],
          }),
        }
      );

      marker.on('mouseover', () => {
        if (
          !window.matchMedia('(hover: hover)').matches
        ) {
          return;
        }

        clearTimeout(leaveTimer.current);
        clearTimeout(enterTimer.current);

        enterTimer.current = setTimeout(
          () => showPeek(venue, marker),
          140
        );
      });

      marker.on('mouseout', leavePeek);

      marker.on('click', () => {
        dismissPeek();
        setSelected(venue);
      });

      group.addLayer(marker);

      markers.set(venue.id, {
        marker,
        venue,
        featured: venue.featured,
        previewVideo,
        url: item?.url,
      });
    });

    pendingFit.current = true;

    if (!loading) {
      fitMap();
    }

    requestAnimationFrame(() => {
      mapState.current?.hydratePins?.();
    });
  }, [
    mapReady,
    venues,
    loading,
    fitMap,
    showPeek,
    dismissPeek,
    leavePeek,
  ]);

  /* User location. */

  useEffect(() => {
    const state = mapState.current;

    if (!state || !mapReady) return;

    state.user.clearLayers();

    if (!location) return;

    const { L, user } = state;

    L.circle(
      [location.lat, location.lng],
      {
        radius: Math.min(
          Math.max(location.accuracy || 0, 20),
          10000
        ),
        weight: 1,
        color: '#666666',
        fillOpacity: 0.08,
        interactive: false,
      }
    ).addTo(user);

    L.circleMarker(
      [location.lat, location.lng],
      {
        radius: 8,
        color: '#fff',
        weight: 3,
        fillColor: '#242424',
        fillOpacity: 1,
      }
    )
      .addTo(user)
      .bindTooltip('Your approximate location');
  }, [mapReady, location]);

  /* Returning to the map preserves position unless a target is selected. */

  useEffect(() => {
    dismissPeek();

    const frame = requestAnimationFrame(() => {
      const state = mapState.current;

      if (
        !state ||
        mode !== 'map' ||
        !focused
      ) {
        return;
      }

      state.map.invalidateSize();

      const venue = target.current;
      target.current = null;

      const marker =
        venue &&
        state.markers.get(venue.id)?.marker;

      if (marker) {
        pendingFit.current = false;

        state.map.setView(
          marker.getLatLng(),
          16,
          { animate: false }
        );

        if (state.group.zoomToShowLayer) {
          state.group.zoomToShowLayer(marker, () => {
            if (alive.current) {
              marker
                .getElement()
                ?.classList.add('cmap-pin-selected');
            }
          });
        } else {
          marker
            .getElement()
            ?.classList.add('cmap-pin-selected');
        }
      } else if (pendingFit.current) {
        fitMap();
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [
    mode,
    focused,
    mapReady,
    fitMap,
    dismissPeek,
  ]);

  /* Native scroll-snap sizing and active video selection. */

  useEffect(() => {
    if (mode !== 'feed' || !feedNode.current) {
      return undefined;
    }

    const root = feedNode.current;

    const size = () => {
      root.style.setProperty(
        '--feed-height',
        `${root.clientHeight}px`
      );
    };

    size();

    const resize = window.ResizeObserver
      ? new ResizeObserver(size)
      : null;

    resize?.observe(root);

    const visible = new Map();

    const observer = window.IntersectionObserver
      ? new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                visible.set(
                  entry.target.dataset.post,
                  entry.intersectionRatio
                );
              } else {
                visible.delete(
                  entry.target.dataset.post
                );
              }
            });

            const best = [
              ...visible.entries(),
            ].sort((a, b) => b[1] - a[1])[0];

            setActivePost(
              best && best[1] >= 0.5
                ? best[0]
                : ''
            );
          },
          {
            root,
            threshold: [0, 0.25, 0.5, 0.7, 0.9, 1],
          }
        )
      : null;

    root
      .querySelectorAll('[data-post]')
      .forEach(el => observer?.observe(el));

    if (!observer) {
      setActivePost(posts[0]?.id || '');
    }

    return () => {
      observer?.disconnect();
      resize?.disconnect();
    };
  }, [mode, limit, posts]);

  /* Load more posts shortly before the end of the current batch. */

  useEffect(() => {
    if (
      !sentinel.current ||
      mode !== 'feed' ||
      !window.IntersectionObserver ||
      limit >= posts.length
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLimit(current =>
            Math.min(
              current + BATCH,
              posts.length
            )
          );
        }
      },
      {
        root: feedNode.current,
        rootMargin: '600px',
      }
    );

    observer.observe(sentinel.current);

    return () => observer.disconnect();
  }, [mode, limit, posts.length]);

  /* Actions. */

  const nearMe = () => {
    setLocationError('');

    if (location || locating) {
      geoRequest.current++;
      setLocation(null);
      setLocating(false);
      return;
    }

    if (
      !window.isSecureContext ||
      !navigator.geolocation
    ) {
      setLocationError(
        'Location needs HTTPS and a browser with location support.'
      );
      return;
    }

    const request = ++geoRequest.current;

    setLocating(true);

    navigator.geolocation.getCurrentPosition(
      position => {
        if (
          !alive.current ||
          request !== geoRequest.current
        ) {
          return;
        }

        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });

        setLocating(false);
      },
      err => {
        if (
          !alive.current ||
          request !== geoRequest.current
        ) {
          return;
        }

        setLocating(false);

        setLocationError(
          err.code === 1
            ? 'Location permission was denied. Allow location for this site, or search an area instead.'
            : 'We could not find your location. Try again or search an area.'
        );
      },
      {
        enableHighAccuracy: false,
        timeout: 12000,
        maximumAge: 60000,
      }
    );
  };

  const clearFilters = () => {
    setQuery('');
    setLocation(null);
    setLocationError('');
    geoRequest.current++;
    setLocating(false);
  };

  const share = async () => {
    const url = ORIGIN + creatorPath(creator);

    try {
      if (
        navigator.share &&
        window.matchMedia('(pointer: coarse)').matches
      ) {
        await navigator.share({
          title: document.title,
          url,
        });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast('Map link copied.');
      } else {
        window.prompt('Copy this map link:', url);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        window.prompt('Copy this map link:', url);
      }
    }
  };

  const showOnMap = venue => {
    target.current = venue;
    setSelected(null);
    setMode('map');
  };

  const creators =
    creator && !data.creators.includes(creator)
      ? [creator, ...data.creators]
      : data.creators;

  const filtered = Boolean(query || location);
  const showEmpty =
    !loading && !error && !venues.length;

  return (
    <div
      ref={rootNode}
      className={`cmap-root cmap-mode-${mode}`}
    >
      <style>{CSS}</style>

      <section
        className="cmap-map-panel"
        hidden={mode !== 'map'}
        aria-label="Creator venue map"
      >
        <div className="cmap-map" ref={mapNode} />
        <div className="cmap-map-wash" />

        <div className="cmap-summary cmap-glass">
          <p className="cmap-eyebrow">
            {creator
              ? 'THE CREATOR’S GUIDE'
              : 'A NEW WAY TO EXPLORE'}
          </p>

          <h1>
            {creator ? (
              <>
                {creator}
                <span>’s map.</span>
              </>
            ) : (
              <>
                Good places.
                <br />
                <span>Through their eyes.</span>
              </>
            )}
          </h1>

          <p className="cmap-summary-count">
            <span className="cmap-dot" />

            {loading
              ? 'Finding the good places…'
              : `${plural(venues.length, 'place')}${
                  location
                    ? ' near you'
                    : ' to explore'
                }`}
          </p>

          {filtered && (
            <button
              type="button"
              className="cmap-reset"
              onClick={clearFilters}
            >
              Clear filters
            </button>
          )}
        </div>

        <button
          type="button"
          className="cmap-icon-button cmap-fit cmap-glass"
          aria-label="Fit all places"
          onClick={fitMap}
          disabled={!mapReady}
        >
          <Icon name="fit" />
        </button>

        {peek && focused && !selected && (
          <article
            className="cmap-peek"
            style={{
              left: peek.x,
              top: peek.y,
            }}
            onMouseEnter={() =>
              clearTimeout(leaveTimer.current)
            }
            onMouseLeave={leavePeek}
          >
            <Media
              key={peek.venue.id}
              item={previewOf(peek.venue)}
              label={`${peek.venue.name} preview`}
              active
              enabled={focused && mode === 'map'}
              poster={posterOf(peek.venue)}
            />

            <button
              type="button"
              className="cmap-peek-info"
              onClick={() => {
                setSelected(peek.venue);
                dismissPeek();
              }}
            >
              <span>
                <strong>{peek.venue.name}</strong>
                <small>
                  {previewOf(peek.venue).creator}
                </small>
              </span>

              <Icon name="arrow" size={18} />
            </button>
          </article>
        )}

        {!loading && mapNearby.length > 0 && (
          <div
            className="cmap-map-rail"
            aria-label="Five nearby creator videos and posts"
          >
            <div className="cmap-map-rail-heading">
              <strong>Around here</strong>
              <span>Creator finds near this spot</span>
            </div>

            <div className="cmap-map-rail-scroll">
              {mapNearby.map(venue => {
                const item = mapPreviewOf(venue);

                return (
                  <button
                    key={venue.id}
                    className="cmap-rail-card"
                    type="button"
                    onClick={() => setSelected(venue)}
                    aria-label={`Explore ${venue.name} by ${item.creator}`}
                  >
                    <div className="cmap-rail-picture">
                      <StillPreview item={item} />

                      <div className="cmap-rail-copy">
                        <strong>{venue.name}</strong>
                        <span>{item.creator}</span>
                      </div>
                    </div>

                    <span className="cmap-rail-more">
                      See more
                      <Icon name="arrow" size={14} />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!mapReady && !mapError && (
          <div className="cmap-map-loading">
            <span className="cmap-spinner" />
            Loading the map…
          </div>
        )}

        {mapError && (
          <div
            className="cmap-map-error cmap-glass"
            role="status"
          >
            {mapError}

            <button
              type="button"
              onClick={() =>
                setMapRetry(current => current + 1)
              }
            >
              Retry map
            </button>
          </div>
        )}
      </section>

      {mode === 'feed' && (
        <div className="cmap-feed-bar">
          <button
            type="button"
            className="cmap-back"
            onClick={() => setMode('map')}
          >
            <Icon name="map" size={17} />
            Back to map
          </button>

          <span>
            {loading
              ? 'Loading…'
              : `${plural(
                  venues.length,
                  'place'
                )} · ${plural(postCount, 'post')}`}
          </span>

          {filtered && (
            <button
              type="button"
              className="cmap-reset"
              onClick={clearFilters}
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      <section
        ref={feedNode}
        className="cmap-feed"
        hidden={mode !== 'feed'}
        aria-label="Creator feed. Scroll to the next post."
        aria-busy={loading}
        tabIndex={0}
      >
        <div className="cmap-feed-inner">
          {loading ? (
            <div className="cmap-skeleton">
              <div />
              <span />
              <span />
            </div>
          ) : (
            posts
              .slice(0, limit)
              .map(({ venue, item, id }, index) => (
                <article
                  key={id}
                  data-post={id}
                  className="cmap-feed-slide"
                  aria-label={`${venue.name}, post ${
                    index + 1
                  } of ${posts.length}`}
                >
                  <div className="cmap-card">
                    <div className="cmap-card-visual">
                      <Media
                        item={item}
                        label={`${venue.name}, by ${item.creator}`}
                        controls
                        active={
                          activePost === id && !selected
                        }
                        enabled={
                          focused &&
                          mode === 'feed' &&
                          !selected
                        }
                      />

                      <div className="cmap-image-title">
                        <span>
                          {[venue.area, venue.city]
                            .filter(Boolean)
                            .join(' · ') ||
                            'Creator discovery'}
                        </span>

                        <h2>
                          <button
                            type="button"
                            onClick={() =>
                              setSelected(venue)
                            }
                          >
                            {venue.name}
                          </button>
                        </h2>
                      </div>

                      <button
                        type="button"
                        className="cmap-count-badge"
                        onClick={() =>
                          setSelected(venue)
                        }
                      >
                        {plural(
                          venue.media.length,
                          'post'
                        )}
                      </button>
                    </div>

                    <div className="cmap-card-body">
                      <div className="cmap-byline">
                        <span className="cmap-avatar">
                          {initials(item.creator)}
                        </span>

                        <button
                          type="button"
                          onClick={() =>
                            chooseCreator(item.creator)
                          }
                        >
                          {item.creator}
                        </button>

                        <span className="cmap-post-position">
                          {index + 1} / {postCount}
                        </span>
                      </div>

                      <div className="cmap-card-actions">
                        <button
                          type="button"
                          className="cmap-explore"
                          onClick={() =>
                            setSelected(venue)
                          }
                        >
                          See more
                          <Icon
                            name="arrow"
                            size={15}
                          />
                        </button>

                        <button
                          type="button"
                          className="cmap-pin-button"
                          aria-label={`Show ${venue.name} on map`}
                          onClick={() =>
                            showOnMap(venue)
                          }
                        >
                          <Icon name="pin" size={17} />
                        </button>

                        <External
                          href={venue.booking_url}
                          className="cmap-visit"
                        >
                          Visit website
                          <Icon
                            name="external"
                            size={14}
                          />
                        </External>
                      </div>

                      {location && (
                        <span className="cmap-distance">
                          {distanceLabel(
                            venue.distance
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              ))
          )}

          {mode === 'feed' &&
            !loading &&
            limit < posts.length && (
              <div
                ref={sentinel}
                className="cmap-more"
              >
                <button
                  type="button"
                  className="cmap-button"
                  onClick={() =>
                    setLimit(current =>
                      Math.min(
                        current + BATCH,
                        posts.length
                      )
                    )
                  }
                >
                  Load more posts
                </button>
              </div>
            )}

          {!loading &&
            postCount > 0 &&
            limit >= postCount && (
              <p className="cmap-feed-end">
                You're all caught up. Explore another
                area on the map.
              </p>
            )}
        </div>
      </section>

      <header
        ref={headerNode}
        className="cmap-header"
      >
        <a
          className="cmap-brand"
          href={ORIGIN}
          aria-label="collabbay home"
        >
          collabbay
          <sup>®</sup>
          <span>THE CREATOR MAP</span>
        </a>

        <div className="cmap-controls cmap-glass">
          <label className="cmap-search">
            <Icon name="search" size={18} />

            <input
              type="search"
              placeholder="Find somewhere good…"
              aria-label="Search places, areas or creators"
              value={query}
              onChange={event =>
                setQuery(event.target.value)
              }
            />
          </label>

          <div className="cmap-filters">
            <select
              aria-label="Choose a creator"
              value={creator}
              onChange={event =>
                chooseCreator(event.target.value)
              }
            >
              <option value="">All creators</option>

              {creators.map(name => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>

            <button
              type="button"
              className={`cmap-near ${
                location ? 'is-on' : ''
              }`}
              onClick={nearMe}
              aria-pressed={Boolean(location)}
            >
              <Icon name="near" size={17} />
              <span>
                {locating ? 'Cancel' : 'Near me'}
              </span>

              {locating && (
                <span className="cmap-spinner" />
              )}
            </button>

            {location && (
              <select
                className="cmap-radius"
                aria-label="Search radius"
                value={radius}
                onChange={event =>
                  setRadius(
                    Number(event.target.value)
                  )
                }
              >
                {[2, 5, 10, 25, 50, 0].map(km => (
                  <option key={km} value={km}>
                    {km
                      ? `${km} km`
                      : 'Any distance'}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <button
          type="button"
          className="cmap-share cmap-icon-button cmap-glass"
          aria-label="Share this creator map"
          onClick={share}
        >
          <Icon name="share" />
        </button>
      </header>

      {showEmpty && (
        <div className="cmap-empty cmap-glass">
          <Icon
            name={location ? 'near' : 'pin'}
            size={30}
          />

          <h2>
            {location
              ? 'Nothing in this radius yet.'
              : 'No places found.'}
          </h2>

          <p>
            {location
              ? 'Try a wider radius or turn off Near me.'
              : filtered
                ? 'Try another name or clear your search.'
                : 'No mapped public content is available for this selection yet.'}
          </p>

          {filtered && (
            <button
              type="button"
              className="cmap-button"
              onClick={clearFilters}
            >
              Clear filters
            </button>
          )}

          {creator && (
            <button
              type="button"
              className="cmap-button"
              onClick={() => chooseCreator('')}
            >
              Show all creators
            </button>
          )}
        </div>
      )}

      {(error || locationError) && (
        <div
          className="cmap-alert cmap-glass"
          role="alert"
        >
          <span>{error || locationError}</span>

          {error ? (
            <button
              type="button"
              onClick={() =>
                setReload(current => current + 1)
              }
            >
              Retry
            </button>
          ) : (
            <button
              type="button"
              aria-label="Dismiss message"
              onClick={() => setLocationError('')}
            >
              <Icon name="close" size={16} />
            </button>
          )}
        </div>
      )}

      <nav
        className="cmap-dock"
        aria-label="Map or feed view"
      >
        <span
          className={`cmap-dock-active ${
            mode === 'feed' ? 'is-feed' : ''
          }`}
          aria-hidden="true"
        />

        {[
          ['map', 'map', 'Map'],
          ['feed', 'feed', 'Feed'],
        ].map(([value, icon, label]) => (
          <button
            key={value}
            type="button"
            aria-pressed={mode === value}
            onClick={() => setMode(value)}
          >
            <Icon name={icon} size={19} />
            <span>{label}</span>
          </button>
        ))}
      </nav>

      {notice && (
        <div className="cmap-toast" role="status">
          {notice}
        </div>
      )}

      {selected && focused && (
        <VenueDialog
          venue={selected}
          onClose={closeDialog}
          onCreator={chooseCreator}
          focused={focused}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Export                                                                     */
/* -------------------------------------------------------------------------- */

export default function CreatorMap(props) {
  if (Platform.OS !== 'web') {
    const creator =
      typeof props.route?.params?.Promoter === 'string'
        ? props.route.params.Promoter
        : '';

    return (
      <View
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f7f7f7',
          padding: 28,
        }}
      >
        <Text
          style={{
            fontSize: 25,
            marginBottom: 14,
          }}
        >
          The creator map
        </Text>

        <Text
          style={{
            textAlign: 'center',
            marginBottom: 20,
          }}
        >
          Open the map and feed in your browser.
        </Text>

        <Pressable
          style={{
            backgroundColor: '#fdf05d',
            padding: 16,
            borderRadius: 14,
          }}
          onPress={() =>
            Linking.openURL(
              ORIGIN + creatorPath(creator)
            )
          }
        >
          <Text>Explore the map</Text>
        </Pressable>
      </View>
    );
  }

  return <CreatorMapWeb {...props} />;
}

/* -------------------------------------------------------------------------- */
/* Styles                                                                     */
/* -------------------------------------------------------------------------- */

const CSS = `
.cmap-root{
  --ink:#202020;
  --muted:#747474;
  --line:#e5e5e5;
  --accent:#fdf05d;
  --head:100px;
  position:relative;
  isolation:isolate;
  width:100%;
  height:100vh;
  height:100dvh;
  overflow:hidden;
  background:#f7f7f7;
  color:var(--ink);
  font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
}

.cmap-root *{box-sizing:border-box}
.cmap-root [hidden]{display:none!important}

.cmap-root button,
.cmap-root input,
.cmap-root select{
  font:inherit;
  color:inherit;
}

.cmap-root button{cursor:pointer;border:0}
.cmap-root button:disabled{opacity:.45;cursor:default}

.cmap-root a,
.cmap-root a:visited{
  color:inherit;
  text-decoration:none;
}

.cmap-root button,
.cmap-root a{
  -webkit-tap-highlight-color:transparent;
  touch-action:manipulation;
}

.cmap-root :is(button,a,input,select):focus-visible{
  outline:2px solid #555;
  outline-offset:4px;
}

.cmap-root h1,
.cmap-root h2,
.cmap-root p{margin:0}

.cmap-root svg{flex:none}

.cmap-glass{
  background:rgba(255,255,255,.93);
  border:1px solid #fff;
  box-shadow:0 4px 24px #0000000b;
  backdrop-filter:blur(20px);
  -webkit-backdrop-filter:blur(20px);
}

/* Header */

.cmap-header{
  position:absolute;
  z-index:700;
  top:20px;
  left:26px;
  right:26px;
  display:grid;
  grid-template-columns:175px minmax(0,780px) 46px;
  justify-content:space-between;
  align-items:center;
  gap:18px;
  pointer-events:none;
}

.cmap-header>*{pointer-events:auto}

.cmap-brand{
  font-size:25px;
  letter-spacing:-1.3px;
  padding:9px 12px;
  border-radius:14px;
  line-height:1.1;
  width:fit-content;
  font-weight:430;
  background:#ffffffed;
}

.cmap-brand sup{
  font-size:10px;
  letter-spacing:0;
  vertical-align:top;
  margin-left:2px;
}

.cmap-brand>span{
  display:block;
  font-size:7px;
  letter-spacing:1.8px;
  font-weight:600;
  margin-top:7px;
  color:var(--muted);
}

.cmap-controls{
  display:flex;
  align-items:center;
  min-width:0;
  border-radius:18px;
  padding:7px 8px 7px 16px;
  min-height:56px;
}

.cmap-search{
  display:flex;
  align-items:center;
  gap:9px;
  flex:1;
  min-width:90px;
  color:var(--muted);
}

.cmap-search input{
  width:100%;
  min-width:0;
  background:none;
  border:0;
  padding:9px 4px 9px 0;
  font-size:13px;
}

.cmap-search input::placeholder{color:#888}

.cmap-filters{
  display:flex;
  align-items:center;
  gap:5px;
  min-width:0;
}

.cmap-filters select{
  background:transparent;
  border:0;
  border-left:1px solid var(--line);
  max-width:175px;
  padding:10px 8px;
  font-size:12px;
  min-width:0;
}

.cmap-filters .cmap-radius{
  max-width:100px;
  font-size:11px;
  padding-left:5px;
}

.cmap-near{
  display:flex;
  align-items:center;
  justify-content:center;
  gap:7px;
  padding:10px 12px;
  border-radius:12px;
  background:#f3f3f3;
  white-space:nowrap;
  font-size:12px!important;
}

.cmap-near.is-on{background:#fff9c9}

.cmap-icon-button{
  width:44px;
  height:44px;
  border-radius:14px;
  display:grid;
  place-items:center;
  background:white;
  border:1px solid var(--line)!important;
  flex:none;
}

.cmap-share{width:46px;height:46px}

.cmap-eyebrow{
  font-size:8px;
  letter-spacing:1.6px;
  font-weight:600;
  color:var(--muted);
  margin-bottom:7px!important;
}

.cmap-button{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:8px;
  padding:10px 14px;
  border-radius:12px;
  background:white;
  border:1px solid var(--line)!important;
  font-size:12px!important;
  min-height:41px;
}

.cmap-dark{
  background:var(--ink)!important;
  color:white!important;
  border-color:var(--ink)!important;
}

/* Map */

.cmap-map-panel,
.cmap-map{
  position:absolute;
  inset:0;
}

.cmap-map{
  z-index:0;
  background:#ededed;
}

.cmap-map-wash{
  position:absolute;
  inset:0;
  pointer-events:none;
  background:linear-gradient(
    180deg,
    #ffffff50,
    transparent 18%,
    transparent 80%,
    #ffffff55
  );
  z-index:400;
}

.cmap-summary{
  position:absolute;
  top:var(--head);
  left:26px;
  border-radius:18px;
  padding:15px 18px;
  max-width:265px;
  z-index:500;
}

.cmap-summary h1{
  font-size:26px;
  letter-spacing:-1px;
  line-height:1.12;
  font-weight:550;
  overflow-wrap:anywhere;
}

.cmap-summary h1 span{
  color:#737373;
  font-weight:400;
}

.cmap-summary-count{
  margin-top:12px!important;
  display:flex;
  gap:8px;
  align-items:center;
  font-size:11px;
  color:#606060;
}

.cmap-dot{
  height:6px;
  width:6px;
  background:var(--accent);
  border:1px solid #c3b83f;
  border-radius:50%;
  flex:none;
}

.cmap-reset{
  background:none;
  text-decoration:underline;
  text-underline-offset:4px;
  padding:0;
  margin-top:7px;
  font-size:11px!important;
}

.cmap-fit{
  position:absolute;
  top:var(--head);
  right:26px;
  z-index:500;
}

.cmap-root .leaflet-tile-pane{
  filter:grayscale(1) brightness(1.035) contrast(.96);
}

.cmap-root .leaflet-container{font:inherit}

.cmap-root .leaflet-bar{
  border:0!important;
  border-radius:12px;
  overflow:hidden;
  box-shadow:0 4px 20px #0001;
}

.cmap-root .leaflet-bar a{
  width:36px;
  height:36px;
  line-height:36px;
  border-bottom-color:var(--line);
  color:var(--ink);
  background:#fffffff5;
}

.cmap-root .leaflet-control-zoom{
  margin-right:26px;
  margin-top:calc(var(--head) + 54px);
  margin-bottom:0;
}

.cmap-root .leaflet-control-attribution{
  background:#fffffff2;
  font-size:9px;
  padding:3px 7px;
  border-radius:6px 0 0 0;
  color:#666;
}

.cmap-root .leaflet-control-attribution a{
  color:#555;
}

/* Thumbnail pins */

.cmap-pin{
  background:none;
  border:0;
}

.cmap-pin-face{
  display:flex;
  align-items:center;
  justify-content:center;
  border:3px solid white;
  background:#e7e7e7;
  color:#555;
  font-size:13px;
  font-weight:600;
  border-radius:15px;
  box-shadow:0 3px 6px #0002,0 9px 20px #0002;
  position:relative;
  transition:transform .18s,box-shadow .18s;
}

.cmap-pin-face:after{
  content:"";
  position:absolute;
  bottom:-7px;
  left:calc(50% - 5px);
  width:10px;
  height:10px;
  background:white;
  transform:rotate(45deg);
  border-radius:2px;
  z-index:-1;
}

.cmap-root .cmap-pin-face>img,
.cmap-root .cmap-pin-face>video{
  height:100%;
  width:100%;
  object-fit:cover;
  border-radius:11px;
  display:block;
  pointer-events:none;
}

.cmap-pin-no-image{
  background:linear-gradient(145deg,#eee,#d5d5d5);
}

.cmap-pin-label{
  position:absolute;
  bottom:0;
  left:0;
  right:0;
  padding:14px 4px 5px;
  background:linear-gradient(transparent,#000b);
  color:white;
  font-size:8px;
  line-height:1.2;
  border-radius:0 0 11px 11px;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
  pointer-events:none;
}

.cmap-pin-subdots{
  position:absolute;
  bottom:-11px;
  right:-9px;
  display:flex;
  flex-direction:row-reverse;
  pointer-events:none;
}

.cmap-pin-subdots>span{
  width:25px;
  height:25px;
  display:grid;
  place-items:center;
  font-size:7px;
  background:#ddd;
  color:#444;
  border:2px solid white;
  border-radius:50%;
  box-shadow:0 2px 6px #0003;
  margin-left:-6px;
  overflow:hidden;
}

.cmap-pin-subdots img{
  width:100%;
  height:100%;
  object-fit:cover;
}

.cmap-pin:hover .cmap-pin-face,
.cmap-pin-selected .cmap-pin-face{
  transform:translateY(-4px);
  box-shadow:
    0 0 0 2px var(--accent),
    0 12px 22px #0003;
}

/* Image-led clusters */

.cmap-cluster{
  background:none;
  border:0;
}

.cmap-cluster-stack{
  width:76px;
  height:82px;
  position:relative;
}

.cmap-cluster-photo{
  position:absolute;
  left:3px;
  top:7px;
  width:64px;
  height:68px;
  border:3px solid white;
  border-radius:15px;
  overflow:hidden;
  background:#e2e2e2;
  box-shadow:0 4px 15px #0003;
  transform:
    translate(
      calc(var(--stack) * 6px),
      calc(var(--stack) * -4px)
    )
    rotate(calc(var(--stack) * 7deg));
}

.cmap-cluster-photo img{
  height:100%;
  width:100%;
  object-fit:cover;
}

.cmap-cluster-count{
  position:absolute;
  bottom:1px;
  right:0;
  min-width:24px;
  height:24px;
  padding:0 5px;
  display:flex;
  align-items:center;
  justify-content:center;
  border:2px solid white;
  background:#272727;
  color:white;
  border-radius:20px;
  font-size:9px;
  font-weight:600;
  box-shadow:0 2px 6px #0002;
}

.cmap-cluster-stack.is-count{
  width:42px;
  height:42px;
  display:grid;
  place-items:center;
  border-radius:50%;
  background:#fff;
  border:3px solid #eee;
  box-shadow:0 3px 12px #0002;
  font-size:12px;
}

/* Nearby cards */

.cmap-map-rail{
  position:absolute;
  z-index:550;
  left:24px;
  right:82px;
  bottom:100px;
  pointer-events:none;
}

.cmap-map-rail-heading{
  display:flex;
  align-items:center;
  gap:12px;
  width:fit-content;
  padding:5px 10px;
  margin-bottom:6px;
  background:#fffffff0;
  border-radius:10px;
  font-size:10px;
  color:#666;
}

.cmap-map-rail-heading strong{
  font-size:12px;
  font-weight:550;
  color:#333;
}

.cmap-map-rail-scroll{
  display:flex;
  gap:12px;
  overflow-x:auto;
  overscroll-behavior-x:contain;
  scroll-snap-type:x mandatory;
  scrollbar-width:none;
  padding:5px 4px 9px;
  pointer-events:auto;
  width:min(100%,750px);
}

.cmap-map-rail-scroll::-webkit-scrollbar{
  display:none;
}

.cmap-rail-card{
  display:flex;
  flex-direction:column;
  flex:0 0 224px;
  width:224px;
  padding:4px;
  text-align:left;
  background:white;
  border:1px solid #fff!important;
  border-radius:19px;
  box-shadow:0 4px 14px #0002;
  scroll-snap-align:start;
  transition:transform .2s;
}

.cmap-rail-card:hover{
  transform:translateY(-3px);
}

.cmap-rail-picture{
  position:relative;
  width:100%;
  height:140px;
  overflow:hidden;
  border-radius:14px;
  background:#e5e5e5;
}

.cmap-still{
  width:100%;
  height:100%;
  object-fit:cover;
  display:block;
  background:#dedede;
}

.cmap-still-fallback{
  display:grid;
  place-items:center;
  color:#fff;
  background:linear-gradient(145deg,#ddd,#999);
}

.cmap-rail-copy{
  position:absolute;
  bottom:0;
  left:0;
  right:0;
  padding:30px 11px 11px;
  background:linear-gradient(transparent,#000b);
  color:white;
  display:flex;
  flex-direction:column;
  gap:2px;
  min-width:0;
}

.cmap-rail-copy strong{
  font-size:15px;
  line-height:1.2;
  font-weight:550;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}

.cmap-rail-copy span{
  font-size:10px;
  opacity:.9;
  white-space:nowrap;
  text-overflow:ellipsis;
  overflow:hidden;
}

.cmap-rail-more{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding:8px 10px 6px;
  font-size:11px;
  font-weight:550;
}

.cmap-rail-more svg{color:#777}

/* Hover previews */

.cmap-peek{
  position:absolute;
  z-index:650;
  width:236px;
  border:4px solid white;
  border-radius:20px;
  background:white;
  overflow:hidden;
  box-shadow:0 12px 40px #0003;
  animation:cmap-rise .16s ease;
}

.cmap-peek .cmap-media{
  height:206px;
  aspect-ratio:auto;
  border-radius:15px;
}

.cmap-peek-info{
  background:white;
  width:100%;
  display:flex;
  align-items:center;
  justify-content:space-between;
  padding:12px 8px 9px;
  text-align:left;
  gap:10px;
}

.cmap-peek-info>span{min-width:0}

.cmap-peek-info strong{
  display:block;
  font-size:13px;
  overflow:hidden;
  white-space:nowrap;
  text-overflow:ellipsis;
}

.cmap-peek-info small{
  font-size:10px;
  display:block;
  margin-top:3px;
  color:var(--muted);
  overflow:hidden;
  white-space:nowrap;
  text-overflow:ellipsis;
}

/* Feed */

.cmap-feed-bar{
  position:absolute;
  left:24px;
  right:24px;
  top:var(--head);
  height:42px;
  z-index:600;
  display:flex;
  align-items:center;
  gap:15px;
}

.cmap-feed-bar>span{
  font-size:11px;
  color:var(--muted);
  margin-left:auto;
}

.cmap-feed-bar .cmap-reset{margin:0}

.cmap-back{
  display:flex;
  align-items:center;
  gap:8px;
  padding:9px 12px;
  background:white;
  border:1px solid var(--line)!important;
  border-radius:12px;
  font-size:12px!important;
}

.cmap-feed{
  position:absolute;
  top:calc(var(--head) + 48px);
  bottom:94px;
  left:0;
  right:0;
  overflow-y:auto;
  overflow-x:hidden;
  overscroll-behavior-y:contain;
  background:#f7f7f7;
  scrollbar-width:thin;
  scrollbar-color:#d4d4d4 transparent;
  scroll-snap-type:y mandatory;
  scroll-padding:0;
  outline:none;
}

.cmap-feed-inner{
  width:100%;
  max-width:620px;
  margin:0 auto;
}

.cmap-feed-slide{
  height:var(--feed-height,650px);
  min-height:300px;
  padding:6px 20px 12px;
  scroll-snap-align:start;
  scroll-snap-stop:always;
  display:flex;
  justify-content:center;
}

.cmap-card{
  width:100%;
  height:100%;
  display:flex;
  flex-direction:column;
  background:white;
  border:1px solid var(--line);
  border-radius:23px;
  overflow:hidden;
  box-shadow:0 3px 18px #00000005;
}

.cmap-card-visual{
  position:relative;
  flex:1;
  min-height:160px;
  background:#eaeaea;
}

.cmap-card-visual>.cmap-media{
  height:100%;
  aspect-ratio:auto;
}

.cmap-card-visual .cmap-media img,
.cmap-card-visual .cmap-media video{
  object-fit:contain;
}

.cmap-image-title{
  position:absolute;
  top:0;
  left:0;
  right:0;
  padding:42px 20px 25px;
  background:linear-gradient(#0009,transparent);
  color:white;
  pointer-events:none;
}

.cmap-image-title>span{
  font-size:10px;
  letter-spacing:.4px;
  opacity:.9;
}

.cmap-image-title h2{
  font-size:clamp(22px,3vw,30px);
  font-weight:550;
  line-height:1.1;
  letter-spacing:-.7px;
  margin-top:5px;
}

.cmap-image-title h2 button{
  pointer-events:auto;
  font:inherit;
  background:none;
  color:white;
  padding:0;
  text-align:left;
  text-shadow:0 1px 4px #0004;
}

.cmap-count-badge{
  position:absolute;
  right:12px;
  top:12px;
  background:#ffffffed;
  border:1px solid white!important;
  padding:5px 8px;
  border-radius:9px;
  font-size:10px!important;
}

.cmap-card-body{
  flex:none;
  padding:12px 17px 14px;
}

.cmap-byline{
  display:flex;
  align-items:center;
  gap:8px;
  min-width:0;
  font-size:12px;
}

.cmap-byline button{
  background:none;
  padding:0;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
  font-size:12px;
}

.cmap-avatar{
  display:inline-grid;
  place-items:center;
  flex:none;
  width:27px;
  height:27px;
  border-radius:50%;
  background:#f0f0f0;
  color:#666;
  font-size:9px;
  font-weight:600;
}

.cmap-post-position{
  margin-left:auto;
  color:#8a8a8a;
  font-size:10px;
  white-space:nowrap;
}

.cmap-card-actions{
  display:flex;
  align-items:center;
  gap:12px;
  margin-top:10px;
  padding-top:10px;
  border-top:1px solid #f0f0f0;
}

.cmap-explore{
  display:inline-flex;
  align-items:center;
  gap:8px;
  background:none;
  padding:6px 0;
  font-size:12px!important;
  font-weight:550;
}

.cmap-pin-button{
  display:grid;
  place-items:center;
  background:#f5f5f5;
  border-radius:9px;
  padding:8px;
}

.cmap-visit{
  display:inline-flex;
  align-items:center;
  gap:5px;
  margin-left:auto;
  border-radius:10px;
  background:#fffbd8;
  padding:8px 10px;
  font-size:11px;
  white-space:nowrap;
}

.cmap-distance{
  display:block;
  color:var(--muted);
  font-size:10px;
  margin-top:6px;
}

.cmap-more{
  text-align:center;
  padding:25px;
  scroll-snap-align:start;
}

.cmap-feed-end{
  text-align:center;
  color:#888;
  font-size:11px;
  padding:25px;
  scroll-snap-align:end;
}

/* Media */

.cmap-media{
  position:relative;
  aspect-ratio:var(--media-ratio,4/5);
  background:#e8e8e8;
  overflow:hidden;
  display:flex;
  align-items:center;
  justify-content:center;
}

.cmap-media img,
.cmap-media video{
  display:block;
  width:100%;
  height:100%;
  object-fit:cover;
}

.cmap-media video{background:#222}

.cmap-media-placeholder,
.cmap-media-empty{
  width:100%;
  height:100%;
  min-height:120px;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap:12px;
  background:linear-gradient(135deg,#f1f1f1,#ddd);
  color:#777;
  padding:20px;
}

.cmap-media-empty strong{
  font-size:13px;
  font-weight:500;
}

.cmap-media-empty a{
  display:inline-flex;
  gap:7px;
  align-items:center;
  font-size:11px;
  padding:8px 12px;
  background:white;
  border:1px solid #ddd;
  border-radius:9px;
}

.cmap-video-tag{
  position:absolute;
  top:12px;
  left:12px;
  display:flex;
  gap:4px;
  align-items:center;
  background:#2229;
  color:white;
  border:1px solid #ffffff35;
  font-size:7px;
  letter-spacing:1.2px;
  padding:4px 6px;
  border-radius:6px;
  pointer-events:none;
}

.cmap-play{
  position:absolute;
  top:50%;
  left:50%;
  transform:translate(-50%,-50%);
  width:46px;
  height:46px;
  border-radius:50%;
  display:grid;
  place-items:center;
  background:#ffffffdd;
  box-shadow:0 5px 20px #0002;
}

/* Loading */

.cmap-skeleton{
  margin:6px 20px;
  border-radius:22px;
  background:white;
  overflow:hidden;
  padding-bottom:25px;
}

.cmap-skeleton>div{
  height:45vh;
  background:linear-gradient(
    100deg,
    #eee 20%,
    #fafafa 45%,
    #eee 70%
  );
  background-size:220% 100%;
  animation:cmap-shimmer 1.5s linear infinite;
}

.cmap-skeleton>span{
  display:block;
  height:12px;
  width:55%;
  margin:20px 20px 0;
  border-radius:5px;
  background:#eee;
}

.cmap-skeleton>span+span{
  width:35%;
  height:8px;
  margin-top:11px;
}

/* Map / feed switch */

.cmap-dock{
  position:absolute;
  left:50%;
  bottom:max(24px,env(safe-area-inset-bottom));
  transform:translateX(-50%);
  z-index:900;
  display:grid;
  grid-template-columns:repeat(2,108px);
  padding:5px;
  border:1px solid #eee;
  border-radius:22px;
  background:#ffffffed;
  box-shadow:0 8px 30px #00000012;
  backdrop-filter:blur(24px);
  -webkit-backdrop-filter:blur(24px);
}

.cmap-dock button{
  display:flex;
  align-items:center;
  justify-content:center;
  gap:9px;
  position:relative;
  z-index:1;
  height:44px;
  background:none;
  border-radius:17px;
  font-size:12px;
  font-weight:500;
  color:#777;
  transition:color .2s;
}

.cmap-dock button[aria-pressed=true]{
  color:white;
}

.cmap-dock button[aria-pressed=true] svg{
  color:var(--accent);
}

.cmap-dock-active{
  position:absolute;
  top:5px;
  bottom:5px;
  left:5px;
  width:108px;
  background:var(--ink);
  border-radius:17px;
  transition:transform .22s cubic-bezier(.2,.8,.2,1);
}

.cmap-dock-active.is-feed{
  transform:translateX(108px);
}

/* Status messages */

.cmap-map-loading{
  position:absolute;
  left:50%;
  top:49%;
  transform:translate(-50%,-50%);
  background:#fffffff0;
  border:1px solid white;
  box-shadow:0 5px 30px #0001;
  padding:15px 22px;
  border-radius:18px;
  display:flex;
  align-items:center;
  gap:10px;
  z-index:500;
  font-size:12px;
}

.cmap-spinner{
  display:inline-block;
  width:15px;
  height:15px;
  border:2px solid #ddd;
  border-top-color:#555;
  border-radius:50%;
  animation:cmap-spin .8s linear infinite;
  flex:none;
}

.cmap-map-error{
  position:absolute;
  bottom:110px;
  left:50%;
  transform:translateX(-50%);
  z-index:600;
  border-radius:15px;
  padding:13px 17px;
  max-width:calc(100% - 28px);
  font-size:12px;
}

.cmap-map-error button{
  background:none;
  text-decoration:underline;
  margin-left:10px;
}

.cmap-empty{
  position:absolute;
  z-index:600;
  top:52%;
  left:50%;
  transform:translate(-50%,-40%);
  border-radius:22px;
  padding:26px;
  text-align:center;
  max-width:350px;
  width:calc(100% - 36px);
  display:flex;
  flex-wrap:wrap;
  gap:12px;
  justify-content:center;
}

.cmap-empty>svg{
  width:100%;
  color:#888;
}

.cmap-empty h2{
  font-size:20px;
  letter-spacing:-.6px;
  font-weight:550;
  width:100%;
}

.cmap-empty p{
  font-size:12px;
  color:var(--muted);
  width:100%;
}

.cmap-alert{
  position:absolute;
  bottom:106px;
  left:50%;
  transform:translateX(-50%);
  z-index:1000;
  display:flex;
  align-items:center;
  gap:15px;
  padding:13px 17px;
  border-radius:15px;
  max-width:calc(100% - 28px);
  width:max-content;
  font-size:12px;
}

.cmap-alert>span{overflow-wrap:anywhere}

.cmap-alert button{
  flex:none;
  background:none;
  text-decoration:underline;
}

.cmap-toast{
  position:absolute;
  z-index:10020;
  bottom:110px;
  left:50%;
  transform:translateX(-50%);
  background:var(--ink);
  color:white;
  padding:12px 19px;
  border-radius:14px;
  box-shadow:0 7px 30px #0002;
  font-size:12px;
}

/* Venue dialog */

.cmap-shade{
  position:absolute;
  inset:0;
  z-index:10000;
  background:#20202080;
  backdrop-filter:blur(8px);
  display:flex;
  align-items:center;
  justify-content:center;
  padding:24px;
}

.cmap-dialog{
  display:flex;
  flex-direction:column;
  max-height:100%;
  width:min(940px,100%);
  background:#fafafa;
  border-radius:26px;
  overflow:hidden;
  box-shadow:0 25px 100px #0004;
  animation:cmap-rise .2s ease;
}

.cmap-dialog-head{
  position:relative;
  padding:24px 28px 20px;
  background:white;
  flex:none;
  border-bottom:1px solid var(--line);
}

.cmap-dialog-head h2{
  font-size:30px;
  font-weight:550;
  letter-spacing:-1px;
  line-height:1.13;
  padding-right:40px;
  overflow-wrap:anywhere;
}

.cmap-address{
  font-size:12px;
  color:var(--muted);
  margin-top:8px!important;
  padding-right:25px;
}

.cmap-dialog-close{
  position:absolute;
  top:19px;
  right:19px;
  width:36px;
  height:36px;
  border-radius:12px;
}

.cmap-dialog-actions{
  display:flex;
  align-items:center;
  gap:8px;
  margin-top:16px;
  flex-wrap:wrap;
}

.cmap-dialog-actions>span{
  font-size:11px;
  color:var(--muted);
  margin-left:auto;
}

.cmap-gallery{
  padding:24px;
  display:grid;
  grid-template-columns:repeat(2,minmax(0,1fr));
  gap:24px;
  overflow:auto;
  min-height:0;
  overscroll-behavior:contain;
  align-items:start;
}

.cmap-gallery>article:only-child{
  grid-column:1/-1;
  max-width:500px;
  justify-self:center;
  width:100%;
}

.cmap-gallery .cmap-byline{
  margin-bottom:10px;
}

.cmap-gallery .cmap-media{
  border-radius:16px;
  max-height:580px;
}

.cmap-gallery .cmap-media img,
.cmap-gallery .cmap-media video{
  object-fit:contain;
}

.cmap-original{
  display:inline-flex;
  align-items:center;
  gap:5px;
  font-size:10px;
  color:#777!important;
  margin-top:9px;
}

/* Animation */

@keyframes cmap-spin{
  to{transform:rotate(360deg)}
}

@keyframes cmap-shimmer{
  to{background-position:-220% 0}
}

@keyframes cmap-rise{
  from{opacity:0;transform:translateY(6px)}
  to{opacity:1;transform:translateY(0)}
}

/* Smaller desktop */

@media(max-width:1100px){
  .cmap-header{
    grid-template-columns:145px minmax(0,1fr) 44px;
    gap:12px;
    left:20px;
    right:20px;
  }

  .cmap-brand{font-size:22px}

  .cmap-filters select{
    max-width:135px;
    font-size:11px;
  }

  .cmap-near{
    padding:10px 9px;
    font-size:11px!important;
  }

  .cmap-controls{padding-left:12px}
  .cmap-summary h1{font-size:23px}
}

/* Mobile */

@media(max-width:760px){
  .cmap-root{--head:160px}

  .cmap-header{
    top:calc(10px + env(safe-area-inset-top,0px));
    left:12px;
    right:12px;
    grid-template-columns:minmax(0,1fr) 40px;
    gap:8px;
  }

  .cmap-brand{
    grid-row:1;
    grid-column:1;
    font-size:23px;
    padding:7px 9px;
    border-radius:12px;
    display:flex;
    align-items:center;
    gap:0;
  }

  .cmap-brand>span{
    font-size:6px;
    letter-spacing:1px;
    max-width:48px;
    line-height:1.6;
    margin:0 0 0 12px;
    padding-left:12px;
    border-left:1px solid var(--line);
  }

  .cmap-brand sup{
    align-self:flex-start;
    font-size:10px;
    line-height:14px;
  }

  .cmap-share{
    grid-row:1;
    grid-column:2;
    width:40px;
    height:40px;
    border-radius:13px;
  }

  .cmap-controls{
    grid-row:2;
    grid-column:1/-1;
    display:block;
    padding:0 11px;
    border-radius:16px;
    min-height:0;
  }

  .cmap-search{
    height:39px;
    border-bottom:1px solid var(--line);
    gap:8px;
  }

  .cmap-search input{
    padding:8px 2px;
    font-size:16px;
  }

  .cmap-filters{
    height:40px;
    gap:5px;
  }

  .cmap-filters select{
    border:0;
    flex:1;
    max-width:none;
    min-width:0;
    font-size:12px;
    padding:6px 0;
  }

  .cmap-filters .cmap-radius{
    max-width:95px;
    flex:0 1 95px;
    padding-left:6px;
  }

  .cmap-near{
    padding:7px 9px;
    border-radius:10px;
    font-size:11px!important;
  }

  .cmap-near svg{width:15px}

  .cmap-summary{
    top:var(--head);
    left:12px;
    padding:9px 12px;
    border-radius:13px;
    max-width:calc(100% - 70px);
  }

  .cmap-summary .cmap-eyebrow,
  .cmap-summary h1{
    display:none;
  }

  .cmap-summary-count{
    margin:0!important;
    font-size:11px;
  }

  .cmap-summary .cmap-reset{
    font-size:10px!important;
    margin-top:4px;
  }

  .cmap-fit{
    right:12px;
    width:35px;
    height:35px;
    border-radius:12px;
  }

  .cmap-map-rail{
    left:10px;
    right:10px;
    bottom:calc(
      88px + env(safe-area-inset-bottom,0px)
    );
  }

  .cmap-map-rail-heading{
    padding:4px 9px;
    gap:8px;
  }

  .cmap-map-rail-heading span{
    font-size:9px;
  }

  .cmap-map-rail-scroll{
    gap:10px;
    width:100%;
    padding-bottom:6px;
  }

  .cmap-rail-card{
    flex-basis:192px;
    width:192px;
    border-radius:17px;
  }

  .cmap-rail-picture{
    height:119px;
    border-radius:13px;
  }

  .cmap-rail-copy strong{
    font-size:14px;
  }

  .cmap-rail-more{
    padding:7px 9px 5px;
    font-size:11px;
  }

  .cmap-root .leaflet-control-zoom{
    margin-right:12px;
    margin-top:calc(var(--head) + 45px);
    margin-bottom:0;
  }

  .cmap-root .leaflet-bar a{
    width:32px;
    height:32px;
    line-height:32px;
  }

  .cmap-root .leaflet-control-attribution{
    font-size:8px;
  }

  .cmap-peek{display:none}

  .cmap-dock{
    bottom:max(23px,env(safe-area-inset-bottom));
    grid-template-columns:repeat(2,96px);
    border-radius:20px;
    padding:5px;
  }

  .cmap-dock button{
    height:40px;
    font-size:12px;
    border-radius:15px;
    gap:8px;
  }

  .cmap-dock-active{
    width:96px;
    border-radius:15px;
  }

  .cmap-dock-active.is-feed{
    transform:translateX(96px);
  }

  .cmap-feed-bar{
    left:12px;
    right:12px;
    height:38px;
    gap:9px;
  }

  .cmap-feed-bar>span{
    font-size:10px;
  }

  .cmap-back{
    font-size:11px!important;
    padding:8px 10px;
  }

  .cmap-feed{
    top:calc(var(--head) + 42px);
    bottom:calc(
      84px + env(safe-area-inset-bottom,0px)
    );
  }

  .cmap-feed-inner{
    max-width:520px;
  }

  .cmap-feed-slide{
    padding:3px 12px 8px;
    min-height:290px;
  }

  .cmap-card{
    border-radius:19px;
  }

  .cmap-card-body{
    padding:9px 13px 10px;
  }

  .cmap-card-actions{
    margin-top:7px;
    padding-top:7px;
    gap:10px;
  }

  .cmap-image-title{
    padding:37px 15px 22px;
  }

  .cmap-image-title h2{
    font-size:24px;
  }

  .cmap-visit{
    font-size:10px;
    padding:7px 9px;
  }

  .cmap-byline button{
    font-size:11px;
  }

  .cmap-avatar{
    height:24px;
    width:24px;
  }

  .cmap-count-badge{
    right:10px;
    top:10px;
  }

  .cmap-distance{
    font-size:9px;
  }

  .cmap-shade{
    padding:8px;
    background:#202020bb;
  }

  .cmap-dialog{
    border-radius:21px;
    height:100%;
    max-height:100%;
  }

  .cmap-dialog-head{
    padding:20px 18px 16px;
    max-height:45%;
    overflow-y:auto;
  }

  .cmap-dialog-head h2{
    font-size:26px;
    padding-right:35px;
  }

  .cmap-dialog-close{
    right:12px;
    top:14px;
  }

  .cmap-address{
    font-size:11px;
  }

  .cmap-dialog-actions{
    gap:6px;
    margin-top:12px;
  }

  .cmap-dialog-actions .cmap-button{
    font-size:11px!important;
    padding:8px 10px;
    min-height:35px;
  }

  .cmap-dialog-actions>span{
    font-size:10px;
  }

  .cmap-gallery{
    grid-template-columns:minmax(0,1fr);
    padding:17px;
    gap:24px;
  }

  .cmap-gallery .cmap-media{
    max-height:65dvh;
  }

  .cmap-toast{
    bottom:95px;
    font-size:11px;
  }

  .cmap-alert{
    font-size:11px;
    bottom:100px;
    gap:10px;
    padding:12px 14px;
  }

  .cmap-map-error{
    font-size:11px;
    bottom:100px;
    width:calc(100% - 28px);
  }
}

/* Short screens */

@media(max-height:620px){
  .cmap-summary h1,
  .cmap-summary .cmap-eyebrow{
    display:none;
  }

  .cmap-summary-count{
    margin:0!important;
  }

  .cmap-map-rail{
    bottom:85px;
  }

  .cmap-rail-picture{
    height:90px;
  }

  .cmap-rail-card{
    flex-basis:170px;
  }

  .cmap-map-rail-heading{
    display:none;
  }

  .cmap-feed-slide{
    min-height:280px;
  }

  .cmap-dock{
    bottom:20px;
  }
}

@media(hover:none){
  .cmap-rail-card:hover{
    transform:none;
  }
}

@media(prefers-reduced-motion:reduce){
  .cmap-root *{
    animation:none!important;
    transition:none!important;
    scroll-behavior:auto!important;
  }
}
`;